import {FC, ExtendProps, getProp} from '@gongxigames/framework/core/fragments/BuilderY';
import {$Sprite, ExtendSpriteProps, SpriteProps} from './Sprite';
import {coreStore} from '@gongxigames/framework/core/globals';
import {RRichText} from '@gongxigames/framework/core/fragments/display/responsive';
import {RichText as RichTextConstructor, RichTextStyleParams, RichTextConstructorOptions} from '@gongxigames/framework/core/fragments/display';
import {LanguageService} from '@gongxigames/framework/core/services/LanguageService';
import {ResourceLoader} from '@gongxigames/framework/core/services/resourceLoader/ResourceLoader';
import {Store} from '@gongxigames/framework/core/fragments/store';

export type {RichTextStyleParams} from '@gongxigames/framework/core/fragments/display';

export type RichTextProps = ExtendSpriteProps<{
    /**
     * string id from [[LanguageService]] strings
     * or
     * any unique id if customText property is defined
     */
    localeID?: string;
    /**
     *  style preset name from [[RichText.styles]]
     *  or
     *  style configs, described in scene *.tsx
    */
    style: string | RichTextStyleParams;
    maxWidth?: number;
    maxHeight?: number;
    text?: string;
    values?: Record<string, string | number>;
    resolution?: number;
}, RichTextConstructor>;

function getOptions(
    props: RichTextProps,
    languageService: LanguageService,
    resourceLoader: ResourceLoader,
): RichTextConstructorOptions {
    return {
        id: getProp(props.localeID)!,
        style: getProp(props.style),
        values: getProp(props.values),
        maxHeight: getProp(props.maxHeight),
        maxWidth: getProp(props.maxWidth),
        customText: getProp(props.text),
        resolution: getProp(props.resolution),

        languageService,
        resourceLoader,
    };
}

/**
 * `RichText` wrapper component.
 * Use `localeID` or `text` to set the content.
 * Use `style` to specify style name or style config.
 * To use the temple features use the `values` property.
 */
export const $RichText: FC<RichTextProps> = (props) => {
    const {languageService, resourceLoader} = coreStore.values();

    let instance: RRichText;

    const off = Store.effect(() => {
        const options = getOptions(props, languageService!, resourceLoader!);

        if (instance) {
            instance.updateText(options);
        } else {
            instance = props.instance ??= new RichTextConstructor(options);
        }
    });

    instance!.on('destroyed', off);

    return $Sprite(props as SpriteProps);
};

// Helper types
export type ExtendRichTextPropsSimple<T> = T & RichTextProps;
export type ExtendRichTextProps<T> = ExtendProps<T, RichTextProps, RichTextConstructor>;
